/* eslint-disable @typescript-eslint/no-inferrable-types */
import {
  AdminProduct,
  PaginatedProductOrderHistories,
  PaginatedProductRecallReport,
  PaginatedProductTransactions,
  ProductSearchSummaries,
} from "types/model.types";
import { authApi, publicApi } from "./";
import { safeApiCall } from "./common.service";
const apiProductPrefix = "product";
/**
 * Payload used to edit an existing product.
 *
 * Contains the primary identifier along with descriptive, classification, and image filename data
 * required to update a product record.
 *
 * @remarks
 * Field formats for `brands` and `categories` (e.g., comma-separated, JSON string, or single value)
 * are determined by the surrounding API/consumer and should be validated/normalized before use.
 *
 * @property id - Unique product identifier. May be a number or a string (for example, a UUID).
 * @property productDesc - Human-readable description or detailed information about the product.
 * @property brands - Brand information associated with the product (names or identifiers as a string).
 * @property categories - Category information associated with the product (names or identifiers as a string).
 * @property dImgName - Filename or path for the product's primary/display image.
 * @property fImgName - Filename or path for the product's secondary/featured image.
 */
export type EditProductPayload = {
  id: string | number;
  productDesc: string;
  brands: string;
  categories: string;
  dImgName: string;
  fImgName: string;
};
/**
 * Fetches product summary records from the backend API.
 *
 * Sends a POST request to the `${apiProductPrefix}/getSummary" endpoint using the authenticated
 * API client and returns the parsed response. If the API returns a nullish value,
 * the function resolves to an empty array.
 *
 * @param mode - Optional mode filter for the query (e.g., "active", "archived"). Defaults to an empty string.
 * @param page - Optional 1-based page number for pagination. Defaults to 1.
 * @param pageSize - Optional number of items per page for pagination. Defaults to 30.
 * @param modifiedAfter - Optional ISO-8601 timestamp string to filter products modified after the given date/time.
 *                        Pass `null` to disable this filter. Defaults to `null`.
 *
 * @returns A Promise that resolves to the API response (typically an array of product summary objects).
 *          If the response is nullish, the promise resolves to an empty array.
 *
 * @throws Will propagate any error thrown by the underlying authenticated API client (e.g., network or auth errors).
 *
 * @example
 * // Fetch first page of active product summaries, 50 items per page, modified after Jan 1, 2024
 * const summaries = await getProductSummaries("active", 1, 50, "2024-01-01T00:00:00Z");
 */
export const getProductSummaries = async (
  mode: string | undefined = "",
  page: number | undefined = 1,
  pageSize: number | undefined = 30,
  modifiedAfter: string | null = null,
) => {
  const response = await authApi.post(`${apiProductPrefix}/getSummary`, {
    modifiedAfter,
    mode,
    page,
    pageSize,
  });
  return response ?? [];
};
/**
 * Fetches the HTML description for a product by its identifier.
 *
 * Sends a POST request to the product/getDescription endpoint and resolves with
 * the HTML content returned by the server.
 *
 * @param id - The product identifier (string | number).
 * @returns A Promise that resolves to the product description as an HTML string.
 * @throws Propagates network or API errors if the request fails.
 * @remarks The returned HTML should be treated as untrusted; sanitize or escape
 *          it before inserting into the DOM to avoid XSS vulnerabilities.
 */
export const getDescription = async (id: string | number) => {
  const res = await publicApi.post(`${apiProductPrefix}/getDescription`, {
    id,
  });
  const html = await res.data;
  return html;
};
/**
 * Sends an authenticated request to update a product.
 *
 * Posts the provided EditProductPayload to the `${apiProductPrefix}/update" endpoint via the authenticated API client
 * and returns the server's response body.
 *
 * @param payload - The product edit payload containing the fields to be updated.
 * @returns A promise that resolves to the response data returned by the API.
 * @throws Will throw if the network request fails or if the API responds with an error status.
 * @example
 * // await updateProduct({ id: "abc123", name: "Updated name", price: 9.99 });
 */
export const updateProduct = async (payload: EditProductPayload) => {
  const response = await authApi.post(
    `${apiProductPrefix}/update`,
    payload,
    {},
  );
  return response.data;
};
/**
 * Update the image file names associated with a product.
 *
 * Sends an authenticated POST request to the `${apiProductPrefix}/updateImageName endpoint
 * with the provided product id and new filenames. The request body uses the
 * keys `fn` (featured name) and `dn` (detailed name).
 *
 * @param id - The product identifier (string or number).
 * @param featuredImageName - New filename to use for the product's featured image.
 * @param detailedImageName - New filename to use for the product's detailed image.
 *
 * @returns A Promise that resolves with the response data from the API (typically
 * the updated product or a success status object).
 *
 * @throws Propagates errors from the underlying request (e.g., network errors or
 * non-2xx HTTP responses from the API).
 *
 * @example
 * // Update images for product with numeric id
 * await updateImageNames(123, "hero.jpg", "gallery1.png");
 *
 * @example
 * // Update images for product with string id
 * await updateImageNames("sku-abc-123", "hero.jpg", "gallery1.png");
 */
export const updateImageNames = async (
  id: string | number,
  featuredImageName: string,
  detailedImageName: string,
) => {
  const response = await authApi.post(
    `${apiProductPrefix}/updateImageNames`,
    { id, fn: featuredImageName, dn: detailedImageName },
    {},
  );
  return response.data;
};

/**
 * Toggle one or more boolean state fields for a product by calling the backend API.
 *
 * Builds a payload containing the provided `id` and any of the optional flags (`f`, `c`, `o`, `s`)
 * that are not `undefined`, then performs a POST to the `${apiProductPrefix}/toggleState" endpoint via `authApi`.
 *
 * Only flags passed explicitly (true or false) are included in the request payload; flags left as
 * `undefined` are omitted and therefore not modified on the server.
 *
 * @param id - The product identifier (string or number).
 * @param f - Optional boolean for the `f` state field. If `undefined`, this field is not sent.
 * @param c - Optional boolean for the `c` state field. If `undefined`, this field is not sent.
 * @param o - Optional boolean for the `o` state field. If `undefined`, this field is not sent.
 * @param s - Optional boolean for the `s` state field. If `undefined`, this field is not sent.
 *
 * @returns A promise that resolves with the API response returned by `authApi.post`.
 *
 * @throws Will propagate any errors thrown by `authApi.post` (e.g., network or server errors).
 *
 * @example
 * // Toggle fields `f` and `o` for product with id 123
 * await toggleState(123, true, undefined, false);
 */
export const toggleState = async (
  id: string | number,
  f?: boolean,
  c?: boolean,
  o?: boolean,
  s?: boolean,
) => {
  const payload: Record<string, any> = { id };
  if (f !== undefined) payload.f = f;
  if (c !== undefined) payload.c = c;
  if (o !== undefined) payload.o = o;
  if (s !== undefined) payload.s = s;

  const response = await safeApiCall(
    authApi.post(`${apiProductPrefix}/toggleState`, payload, {}),
  );
  return response;
};

/**
 * Admin section
 */
export const getProductOrderHistories = async (
  id: number,
  pageNumber: number = 1,
  pageSize: number = 30,
): Promise<void | PaginatedProductOrderHistories> => {
  const { data } = await authApi.post<PaginatedProductOrderHistories>(
    `${apiProductPrefix}/getProductOrderHistories`,
    {
      id,
      pageNumber,
      pageSize,
    },
  );
  return data;
};
export const getProductTransactions = async (
  id: number,
  pageNumber: number = 1,
  pageSize: number = 30,
): Promise<void | PaginatedProductTransactions> => {
  const { data } = await authApi.post<PaginatedProductTransactions>(
    `${apiProductPrefix}/getProductTransactions`,
    {
      id,
      pageNumber,
      pageSize,
    },
  );
  return data;
};

/**
 * Admin section
 */
export const getProduct = async (id: number): Promise<void | AdminProduct> => {
  const { data } = await authApi.post<AdminProduct>(`${apiProductPrefix}/get`, {
    id,
  });
  return data;
};
export const submitProduct = async (
  payload: AdminProduct,
): Promise<void | AdminProduct> => {
  const { data } = await authApi.post<AdminProduct>(
    `${apiProductPrefix}/submit`,
    {
      payload,
    },
  );
  return data;
};

export const quickSearch = async (
  searchTerm: string,
): Promise<void | ProductSearchSummaries> => {
  const { data } = await authApi.post<ProductSearchSummaries>(
    `${apiProductPrefix}/search`,
    {
      searchTerm,
    },
  );
  return data;
};

export const productRecallSearch = async (
  pageSize = 50,
  pageNumber = 1,
  id: number,
  downloadCsv: boolean = false,
  dateFrom: Date | null,
  dateTo: Date | null,
  productCodeFrom: number | undefined,
  productCodeTo: number | undefined,
): Promise<void | PaginatedProductRecallReport> => {
  if (downloadCsv) {
    // CSV mode
    const response = await authApi.post(
      `${apiProductPrefix}/productRecall`,
      {
        id,
        pageSize,
        pageNumber,
        dateFrom,
        dateTo,
        productCodeFrom,
        productCodeTo,
        downloadCsv: true,
      },
      { responseType: "blob" },
    );
    const blob = new Blob([response.data], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `ProductRecall.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
    return;
  }
  const { data } = await authApi.post<PaginatedProductRecallReport>(
    `${apiProductPrefix}/productRecall`,
    {
      id,
      pageSize,
      pageNumber,
      dateFrom,
      dateTo,
      downloadCsv: false,
      productCodeFrom,
      productCodeTo,
    },
  );
  return data ?? [];
};
